import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { IoCall } from "react-icons/io5";
import { FaEdit, FaSearch, FaCalendarAlt } from "react-icons/fa";
import { myLeads, getAllLeads, automaticCall, updateLead, resendERCode } from "../Api/api";
import { toast } from "react-toastify";
import { useAuth } from "../Auth/AuthContext";
import { useRegions } from '../../context/RegionContext';
import LeadUpdateModal from "./LeadUpdateModal";
import AdminViewToggle from "../Common/AdminViewToggle";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import DownloadTableData from '../Common/DownloadTableData';
import { MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';

const PendingRegistration = () => {
  const { user, logout, selectedRegion } = useAuth();
  const { getRegionName } = useRegions();
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(20);
  const [filter, setFilter] = useState("all");
  const [currentItems, setCurrentItems] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAllView, setShowAllView] = useState(false);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchType, setSearchType] = useState("name");
  const [searchOffset, setSearchOffset] = useState(0);
  const [searchLimit] = useState(20);
  const [isSearching, setIsSearching] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [visiblePhoneId, setVisiblePhoneId] = useState(null);
  
  const formatDate = (incomingDate) => {
    if (!incomingDate) return '';
    return moment(incomingDate).format('DD-MM-YYYY');
  };
  
  const convertTo12HourFormat = (incomingTime) => {
    if (!incomingTime) return '';
    return moment(incomingTime, 'HH:mm').format('hh:mm A');
  };
  
  const getSourcingLeads = async (
    limit,
    offset,
    from_date = null,
    to_date = null,
    search = null,
    status = null,
  ) => {
    try {
      setIsLoading(true);
      const selectedRegion = localStorage.getItem("selectedRegion") || "1";
      const region_ids = selectedRegion;
      const fetchFunction = showAllView ? getAllLeads : myLeads;
      const response = await fetchFunction(
        limit,
        offset,
        from_date,
        to_date,
        search,
        status,
        region_ids
      );
      return response;
    } catch (error) {
      console.error("Failed to get sourcing leads:", error);
      toast.error("Failed to get sourcing leads");
      return { items: [], total: 0 };
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialData = async () => {
    const response = await getSourcingLeads(
      itemsPerPage,
      0,
      fromDate?.toISOString(),
      toDate?.toISOString(),
      null,
      "QUALIFIED"
    );
    setAllItems(response.items || []);
    setTotalItems(response.total || 0);
    setCurrentItems(response.items || []);
  };

  const handleDateFilter = async () => {
    setCurrentPage(0);
    await loadInitialData();
  };

  const handlePageClick = async (event) => {
    const newPage = event.selected;
    setCurrentPage(newPage);

    const response = await getSourcingLeads(
      itemsPerPage,
      newPage * itemsPerPage,
      fromDate?.toISOString(),
      toDate?.toISOString(),
      null,
      "QUALIFIED"
    );
    setCurrentItems(response.items || []);
    setAllItems(response.items || []);
    setTotalItems(response.total || 0);
  };

  useEffect(() => {
    loadInitialData();
  }, [showAllView, fromDate, toDate]);
  
  useEffect(() => {
    // Filter registrations based on selected region
    if (currentItems && currentItems.length > 0) {
      const selectedRegionName = getRegionName(selectedRegion);
      const filtered = currentItems.filter(item => {
        const geo = item.details_dict?.CLUSTER?.toLowerCase() || '';
        return geo.includes(selectedRegionName);
      });
      setFilteredRegistrations(filtered);
    } else {
      setFilteredRegistrations([]);
    }
  }, [currentItems, selectedRegion, getRegionName]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    searchItems(searchQuery, searchType);
  };
  
  const isMobile = () => {
    const userAgent = navigator.userAgent || navigator.platform;
    return /Mobi|Android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent);
  };
  
  const clickToCall = async (phoneNumber) => {
    if (isMobile()) {
      window.location.href = `tel:+91${phoneNumber}`;
    } else {
      navigator.clipboard
        .writeText(phoneNumber)
        .then(() => {
          toast.success("Phone number copied to clipboard");
        })
        .catch((err) => {
          console.error(err);
          toast.error("Error copying phone number");
        });
    }
  };

  const handleUpdateLead = async (leadId, updateData) => {
    try {
      await updateLead(leadId, updateData);
      toast.success("Lead updated successfully");
      // Refresh the leads list
      loadInitialData();
    } catch (error) {
      console.error("Failed to update lead:", error);
      toast.error(error?.response?.data?.errors?.[0]?.message || "Failed to update lead");
    }
  };

  const searchItems = async (query, type) => {
    if (!query) {
      loadInitialData();
      return;
    }

    setIsSearching(true);

    if (type === "phone") {
      // Use backend phone search - single API call
      const response = await getSourcingLeads(itemsPerPage, 0, null, null, query, "QUALIFIED");
      setCurrentItems(response.items || []);
      setTotalItems(response.total || 0);
    } else {
      // For name/agent, filter within the already-loaded items client-side
      const searchValue = query.toLowerCase();
      const filtered = allItems.filter(item => {
        if (type === "name") {
          return item.name?.toLowerCase().includes(searchValue);
        } else if (type === "agent") {
          return item.agent_name?.toLowerCase().includes(searchValue);
        }
        return false;
      });
      setCurrentItems(filtered);
      setTotalItems(filtered.length);
    }

    setIsSearching(false);
  };

  const tableHeaders = [
    "Agent",
    "Full Name",
    "Phone",
    "Call",
    "Status",
    "Date of Registration",
    "Scheduled Date",
    "Scheduled Time",
    "Geo"
  ];

  const formatTableData = (data) => {
    return data.map(item => [
      item.agent_name || "N/A",
      item.name || "",
      item.phone || "",
      "", // Call column is empty in CSV
      item.status || "",
      formatDate(item.created_at),
      formatDate(item.details_dict?.REGISTRATION_DATE),
      convertTo12HourFormat(item.details_dict?.REGISTRATION_TIME),
      item.details_dict?.CLUSTER || "Did Not Answer"
    ]);
  };

  const handlePhoneVisibility = (id) => {
    setVisiblePhoneId(visiblePhoneId === id ? null : id);
  };

  return (
    <div className="container mx-auto my-8 p-4">
      <div className="flex flex-col gap-6">
        {/* Date Filter Section */}
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-semibold mb-2">Date Filter</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1">
              <FaCalendarAlt className="text-gray-500 text-sm" />
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate || new Date()}
                placeholderText="From Date"
                dateFormat="dd/MM/yyyy"
                className="px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <div className="flex items-center gap-1">
              <FaCalendarAlt className="text-gray-500 text-sm" />
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                maxDate={new Date()}
                placeholderText="To Date"
                dateFormat="dd/MM/yyyy"
                className="px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <button
              onClick={handleDateFilter}
              className="px-3 py-1 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Apply Filter
            </button>
            <button
              onClick={() => {
                setFromDate(null);
                setToDate(null);
                handleDateFilter();
              }}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filter
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          {/* Search Section */}
          <form onSubmit={handleSearch} className="w-full md:w-1/3">
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="px-3 py-2 border border-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-pink-500 bg-white text-gray-700 min-w-[160px] text-sm"
                >
                  <option value="agent">Search by Agent</option>
                  <option value="name">Search by Name</option>
                  <option value="phone">Search by Phone</option>
                </select>
                <div className="flex items-center gap-2 flex-1">
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                      <FaSearch className="w-4 h-4 opacity-60" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search by..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-pink-500 bg-white placeholder-gray-400 text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    className="h-[38px] w-[38px] bg-pink-500 text-white rounded-lg hover:bg-pink-600 focus:outline-none focus:ring-1 focus:ring-pink-500 flex items-center justify-center"
                    aria-label="Search"
                  >
                    <FaSearch className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Filter Section */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setCurrentPage(0);
                loadInitialData();
                setFilter("all");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "all"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              All
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const to_date = today.toISOString();
                today.setDate(today.getDate() - 7);
                const from_date = today.toISOString();
                setCurrentPage(0);
                getSourcingLeads(
                  itemsPerPage,
                  0,
                  from_date,
                  to_date,
                  null,
                  "QUALIFIED"
                ).then((response) => {
                  setCurrentItems(response.items || []);
                  setTotalItems(response.total || 0);
                });
                setFilter("hot");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "hot"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              Hot (L7)
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const to_date = new Date(today);
                to_date.setDate(today.getDate() - 8);
                const from_date = new Date(today);
                from_date.setDate(today.getDate() - 15);
                const to_date_iso = to_date.toISOString();
                const from_date_iso = from_date.toISOString();
                setCurrentPage(0);
                getSourcingLeads(
                  itemsPerPage,
                  0,
                  from_date_iso,
                  to_date_iso,
                  null,
                  "QUALIFIED"
                ).then((response) => {
                  setCurrentItems(response.items || []);
                  setTotalItems(response.total || 0);
                });
                setFilter("warm");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "warm"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              Warm (L8-15)
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const to_date = new Date(today);
                to_date.setDate(today.getDate() - 16);
                const to_date_iso = to_date.toISOString();
                setCurrentPage(0);
                getSourcingLeads(
                  itemsPerPage,
                  0,
                  null,
                  to_date_iso,
                  null,
                  "QUALIFIED"
                ).then((response) => {
                  setCurrentItems(response.items || []);
                  setTotalItems(response.total || 0);
                });
                setFilter("cold");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "cold"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              Cold (L15+)
            </button>
          </div>

          {/* Admin View Toggle */}
          <AdminViewToggle
            showAllView={showAllView}
            onToggle={() => setShowAllView(!showAllView)}
          />
        </div>

        {/* Loading State */}
        {(isLoading || isSearching) && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
          </div>
        )}

        {/* Table with Registrations */}
        {!isLoading && !isSearching && (
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">
                Pending Registration
              </h2>
              <DownloadTableData
                data={formatTableData(filteredRegistrations)}
                headers={tableHeaders}
                filenamePrefix="pending_registration"
                showAllView={showAllView}
              />
            </div>
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Phone Number
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Call
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Date Of Early Registration
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Scheduled Date
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Scheduled Time
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Geo
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Resend Er Code
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredRegistrations.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4">{item.agent_name || "N/A"}</td>
                    <td className="px-6 py-4">{item.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePhoneVisibility(item.id)}
                          className={`p-2 rounded-lg transition-all duration-200 hover:bg-emerald-50 ${
                            visiblePhoneId === item.id
                              ? "text-emerald-600 bg-emerald-50"
                              : "text-emerald-500"
                          }`}
                          title={
                            visiblePhoneId === item.id
                              ? "Hide Phone Number"
                              : "Show Phone Number"
                          }
                        >
                          {visiblePhoneId === item.id ? (
                            <div className="flex items-center gap-3">
                              <span
                                className="no-select text-gray-700 font-medium"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  msUserSelect: "none",
                                  MozUserSelect: "none",
                                }}
                              >
                                {item.phone}
                              </span>
                              <MdOutlineVisibilityOff className="w-5 h-5 hover:text-emerald-700" />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-full">
                              <MdOutlineVisibility className="w-5 h-5 hover:text-emerald-700" />
                            </div>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        className="text-pink-500 hover:text-pink-700"
                        onClick={() => clickToCall(item.phone)}
                      >
                        <IoCall />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          item.status === "QUALIFIED"
                            ? "bg-green-100 text-green-800"
                            : item.status === "HOLD"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`} ///102247
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">{formatDate(item.created_at)}</td>
                    <td className="px-6 py-4">
                      {formatDate(item.details_dict?.REGISTRATION_DATE)}
                    </td>
                    <td className="px-6 py-4">
                      {convertTo12HourFormat(
                        item.details_dict?.REGISTRATION_TIME
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {item.details_dict?.CLUSTER || "Did Not Answer"}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => resendERCode(item.phone)}
                        className="bg-green-600 hover:bg-green-900 text-white font-semibold py-2 px-4 rounded"
                        title="Re-send ER Code"
                      >
                        Re Send
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {item.status === "QUALIFIED" && (
                        <button
                          onClick={() => setSelectedLead(item)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Lead"
                        >
                          <FaEdit className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="sm:hidden">
              {filteredRegistrations.map((item) => (
                <div key={item.id} className="bg-white p-4 border-b">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="text-gray-600 mb-1">
                        Agent: {item.agent_name || "N/A"}
                      </p>
                      <h3 className="font-medium text-base">{item.name}</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePhoneVisibility(item.id)}
                          className={`p-2 rounded-lg transition-all duration-200 hover:bg-emerald-50 ${
                            visiblePhoneId === item.id
                              ? "text-emerald-600 bg-emerald-50"
                              : "text-emerald-500"
                          }`}
                          title={
                            visiblePhoneId === item.id
                              ? "Hide Phone Number"
                              : "Show Phone Number"
                          }
                        >
                          {visiblePhoneId === item.id ? (
                            <div className="flex items-center gap-3">
                              <span
                                className="no-select text-gray-700 font-medium"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  msUserSelect: "none",
                                  MozUserSelect: "none",
                                }}
                              >
                                {item.phone}
                              </span>
                              <MdOutlineVisibilityOff className="w-5 h-5 hover:text-emerald-700" />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-full">
                              <MdOutlineVisibility className="w-5 h-5 hover:text-emerald-700" />
                            </div>
                          )}
                        </button>
                      </div>
                      <div className="mt-1">
                        <span
                          className={`inline-block px-2 py-1 text-xs rounded-full ${
                            item.status === "QUALIFIED"
                              ? "bg-green-100 text-green-800"
                              : item.status === "HOLD"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        className="text-pink-500 hover:text-pink-700"
                        onClick={() => clickToCall(item.phone)}
                      >
                        <IoCall className="w-6 h-6" />
                      </button>
                      {item.status === "QUALIFIED" && (
                        <button
                          onClick={() => setSelectedLead(item)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <FaEdit className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="grid grid-cols-2 gap-1">
                      <p className="text-gray-500">Early Registration:</p>
                      <p>{formatDate(item.created_at)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <p className="text-gray-500">Scheduled Date:</p>
                      <p>{formatDate(item.details_dict?.REGISTRATION_DATE)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <p className="text-gray-500">Scheduled Time:</p>
                      <p>
                        {convertTo12HourFormat(
                          item.details_dict?.REGISTRATION_TIME
                        )}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <p className="text-gray-500">Geo:</p>
                      <p>{item.details_dict?.CLUSTER || "Did Not Answer"}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !isSearching && totalItems > itemsPerPage && (
          <div className="mt-6 flex justify-center">
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={Math.ceil(totalItems / itemsPerPage)}
              previousLabel="< Previous"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center space-x-2"
              pageClassName="px-3 py-1 rounded hover:bg-pink-100"
              pageLinkClassName="text-gray-700"
              activeClassName="bg-pink-500 text-white"
              previousClassName="px-3 py-1 rounded hover:bg-pink-100"
              nextClassName="px-3 py-1 rounded hover:bg-pink-100"
              disabledClassName="opacity-50 cursor-not-allowed"
              forcePage={currentPage}
            />
          </div>
        )}

        {selectedLead && (
          <LeadUpdateModal
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
            onUpdate={(updateData) =>
              handleUpdateLead(selectedLead.id, updateData)
            }
          />
        )}
      </div>
    </div>
  );
};

export default PendingRegistration;
